import { type FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { setActiveOrgSlug } from '@/lib/activeOrg';
import { useCreateOrg } from '@/lib/orgs';

function autoSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
}

export function CreateOrgPage() {
  const create = useCreateOrg();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [prefix, setPrefix] = useState('AR');

  const effectiveSlug = slugTouched ? slug : autoSlug(name);

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!name.trim()) return;
    try {
      const created = await create.mutateAsync({
        name: name.trim(),
        slug: effectiveSlug || undefined,
        part_number_prefix: prefix.trim() || 'AR',
      });
      // Switch the user into the new org and land on its members page.
      setActiveOrgSlug(created.slug);
      navigate(`/orgs/${created.slug}`);
    } catch {
      /* surfaced below */
    }
  };

  return (
    <div className="mx-auto max-w-xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Create organization</h1>
        <p className="mt-1 text-stone-600 dark:text-stone-400">
          A shared workspace for a small team. You'll be the owner; invite
          members from the org page once it's created.
        </p>
      </div>

      <form
        onSubmit={onSubmit}
        className="space-y-4 rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 p-5"
      >
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-stone-700 dark:text-stone-300">Name</span>
          <Input
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Saturday Builders"
          />
        </label>

        <label className="block">
          <span className="mb-1 block text-sm font-medium text-stone-700 dark:text-stone-300">
            Slug{' '}
            <span className="font-normal text-stone-500 dark:text-stone-400">
              (URL identifier; auto from name unless you override)
            </span>
          </span>
          <Input
            value={effectiveSlug}
            onChange={(e) => {
              setSlug(e.target.value);
              setSlugTouched(true);
            }}
            placeholder="saturday-builders"
            pattern="[a-z0-9](?:[a-z0-9-]{0,98}[a-z0-9])?"
          />
        </label>

        <label className="block">
          <span className="mb-1 block text-sm font-medium text-stone-700 dark:text-stone-300">
            Part number prefix
          </span>
          <Input
            value={prefix}
            onChange={(e) => setPrefix(e.target.value.toUpperCase())}
            maxLength={16}
            className="w-32 font-mono"
          />
          <span className="mt-1 block text-xs text-stone-500 dark:text-stone-400">
            Parts created in this org get numbers like {prefix || 'AR'}10000.
          </span>
        </label>

        {create.isError && (
          <p className="text-sm text-red-600">{(create.error as Error).message}</p>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="ghost" type="button" onClick={() => navigate('/')}>
            Cancel
          </Button>
          <Button type="submit" disabled={create.isPending || !name.trim()}>
            {create.isPending ? 'Creating…' : 'Create organization'}
          </Button>
        </div>
      </form>
    </div>
  );
}
